import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, Plus, Trash2, Ban, KeyRound, LogOut, Power } from "lucide-react";

/** iter86.75 — Commons → Mezzanine → Mobile Testers: platform-level Mobile ID
 * governance (invites, account CRUD, program switch). TESTER_MOBILE_STANDIN.md §3 */
export default function MobileTestersTab() {
    const [data, setData] = useState({ program_enabled: false, users: [], invites: [] });
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState("");
    const [claims, setClaims] = useState({});
    const [tenants, setTenants] = useState([]);
    const [tenantId, setTenantId] = useState("");

    useEffect(() => {
        api.listTenants().then((r) => setTenants(r.tenants || [])).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        try { setData(await api.platMobileTesters(tenantId || null)); }
        catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
        setLoading(false);
    }, [tenantId]);
    useEffect(() => { load(); }, [load]);

    const tenantName = (id) => (tenants.find((t) => t.id === id) || {}).name || (id ? id.slice(0, 8) : "—");

    const act = async (fn, okMsg) => {
        try { await fn(); toast.success(okMsg); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const resetPassword = (u) => {
        const pw = window.prompt(`New password for ${u.username} (min 8 chars):`);
        if (!pw) return;
        act(() => api.platMobileTesterPatch(u.id, { reset_password: pw }), "Password reset (all sessions revoked)");
    };

    const viewClaims = async (u) => {
        if (claims[u.id]) { setClaims((c) => ({ ...c, [u.id]: null })); return; }
        const r = await api.platMobileTesterClaims(u.id);
        setClaims((c) => ({ ...c, [u.id]: r.claims || [] }));
    };

    return (
        <div className="brutal-card p-5 space-y-4" data-testid="mobile-testers-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-cyber" />
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wider">Mobile Testers</h3>
                </div>
                <button
                    data-testid="mobile-program-toggle-btn"
                    onClick={() => act(() => api.platMobileTesterProgram(!data.program_enabled),
                        data.program_enabled ? "Program disabled — /tester is now hidden" : "Program enabled")}
                    className={`brutal-btn text-xs py-1.5 font-bold ${data.program_enabled ? "bg-neon text-black" : "bg-heat text-black"}`}>
                    <Power className="w-3 h-3" /> Program: {data.program_enabled ? "ON" : "OFF"}
                </button>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Platform back-office for the <strong className="text-white">/tester</strong> mobile stand-in (per environment).
                Invites gate enrollment; rivalry-level connection revokes live in each tenant's Site Admin → Mobile Access.
            </p>

            <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 shrink-0">Tenant</span>
                <select data-testid="mobile-testers-tenant-select" className="brutal-input text-xs py-1.5 max-w-xs"
                    value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                    <option value="">All tenants</option>
                    {tenants.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            </div>

            <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Invites</p>
                <div className="flex gap-2">
                    <input data-testid="mobile-invite-note-input" className="brutal-input flex-1 text-sm" placeholder="Note (who is this for?)"
                        value={note} onChange={(e) => setNote(e.target.value)} />
                    <button data-testid="mobile-invite-create-btn"
                        onClick={() => act(() => api.platMobileTesterInviteCreate(note, tenantId || null).then(() => setNote("")), tenantId ? `Invite created for ${tenantName(tenantId)}` : "Invite created")}
                        className="brutal-btn text-xs py-2 bg-cyber text-black font-bold"><Plus className="w-3.5 h-3.5" /> New invite</button>
                </div>
                {(data.invites || []).filter((i) => i.active && !i.used_by).map((i) => (
                    <div key={i.id} className="flex items-center gap-3 p-2 border-2 border-[#333] rounded-md text-sm" data-testid={`mobile-invite-${i.id}`}>
                        <code className="font-mono font-bold text-volt">{i.code}</code>
                        <span className="text-[9px] uppercase tracking-widest text-cyber shrink-0">{tenantName(i.tenant_id)}</span>
                        <span className="text-xs text-zinc-500 flex-1 truncate">{i.note || "—"}</span>
                        <button data-testid={`mobile-invite-revoke-${i.id}`} onClick={() => act(() => api.platMobileTesterInviteRevoke(i.id), "Invite revoked")}
                            className="brutal-btn-ghost text-xs py-1 px-2 text-heat"><Ban className="w-3 h-3" /></button>
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Accounts</p>
                {loading && <p className="text-xs text-zinc-500">Loading…</p>}
                {!loading && (data.users || []).length === 0 && <p className="text-sm text-zinc-500" data-testid="mobile-testers-empty">No tester accounts yet.</p>}
                {(data.users || []).map((u) => (
                    <div key={u.id} className={`p-2.5 border-2 rounded-md space-y-1.5 ${u.disabled ? "border-heat/40 opacity-70" : "border-[#333]"}`} data-testid={`mobile-tester-row-${u.id}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                            <button onClick={() => viewClaims(u)} className="font-bold text-sm hover:text-cyber" data-testid={`mobile-tester-claims-btn-${u.id}`}>{u.username}</button>
                            <span className="text-xs text-zinc-500">{u.claim_count} claim(s) · {u.invite_note || "no note"}</span>
                            {(u.tenant_ids || []).map((tid) => (
                                <span key={tid} className="text-[9px] uppercase tracking-widest text-cyber border border-cyber/30 px-1 rounded">{tenantName(tid)}</span>
                            ))}
                            <span className={`text-[9px] uppercase tracking-widest font-bold ${u.disabled ? "text-heat" : "text-neon"}`}>{u.disabled ? "Disabled" : "Active"}</span>
                            <div className="ml-auto flex items-center gap-1">
                                <button title="Reset password" data-testid={`mobile-tester-resetpw-${u.id}`} onClick={() => resetPassword(u)} className="brutal-btn-ghost text-xs py-1 px-2 text-zinc-400 hover:text-volt"><KeyRound className="w-3 h-3" /></button>
                                <button title="Force logout" data-testid={`mobile-tester-logout-${u.id}`} onClick={() => act(() => api.platMobileTesterPatch(u.id, { force_logout: true }), "All sessions revoked")} className="brutal-btn-ghost text-xs py-1 px-2 text-zinc-400 hover:text-volt"><LogOut className="w-3 h-3" /></button>
                                <button title={u.disabled ? "Re-enable" : "Disable"} data-testid={`mobile-tester-disable-${u.id}`} onClick={() => act(() => api.platMobileTesterPatch(u.id, { disabled: !u.disabled }), u.disabled ? "Account re-enabled" : "Account disabled")} className="brutal-btn-ghost text-xs py-1 px-2 text-heat"><Ban className="w-3 h-3" /></button>
                                <button title="Delete account + all claims" data-testid={`mobile-tester-delete-${u.id}`} onClick={() => { if (window.confirm(`Delete ${u.username} and ALL their claims?`)) act(() => api.platMobileTesterDelete(u.id), "Account deleted"); }} className="brutal-btn-ghost text-xs py-1 px-2 text-heat"><Trash2 className="w-3 h-3" /></button>
                            </div>
                        </div>
                        {claims[u.id] && (
                            <div className="pl-2 border-l-2 border-[#333] space-y-1" data-testid={`mobile-tester-claims-${u.id}`}>
                                {claims[u.id].length === 0 && <p className="text-xs text-zinc-500">No claims.</p>}
                                {claims[u.id].map((c) => (
                                    <p key={c.membership_id} className="text-xs text-zinc-400">
                                        {c.league_name} · as <strong className="text-white">{c.participant_username || "?"}</strong> ({c.role === "admin" ? "Admin" : "Crewmate"}) · <span className={c.active ? "text-neon" : "text-heat"}>{c.active ? "active" : "revoked"}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
